import { toSortableTimestamp } from "~/lib/threadSort";

import type { PullRequestInboxGroup, PullRequestInboxRow } from "./pullRequests.inbox.logic";

/**
 * The inbox orderings, adopted from t3code's `pullRequestList.logic` and
 * `pullRequestListPreferences`. Forge's inbox is URL-search driven rather than preference
 * persisted, so the sort travels in the search the route already owns; the ranks themselves are
 * the upstream tiers, applied inside the Reviewing / Authored / Others sections forge already
 * groups by.
 */
const PULL_REQUEST_LIST_SORTS = [
  "ready",
  "blocked",
  "updated",
  "newest",
  "oldest",
  "largest",
  "smallest",
] as const;

export type PullRequestListSort = (typeof PULL_REQUEST_LIST_SORTS)[number];

export function parsePullRequestListSort(value: unknown): PullRequestListSort | undefined {
  return typeof value === "string" && (PULL_REQUEST_LIST_SORTS as readonly string[]).includes(value)
    ? (value as PullRequestListSort)
    : undefined;
}

function hasMeasuredSize(row: PullRequestInboxRow): boolean {
  return row.additions + row.deletions > 0;
}

function mergeReadinessTier(row: PullRequestInboxRow): number {
  if (row.mergeability === "conflicting") return 4;
  if (row.state !== "open") return 3;
  if (row.isDraft) return 2;
  if (row.checksState === "passing" && row.reviewDecision === "approved") return 0;
  if (row.checksState === "passing") return 1;
  return 2;
}

function sortableUpdatedAt(row: PullRequestInboxRow): number | null {
  return toSortableTimestamp(row.updatedAt);
}

/** Recency as a tiebreak: the more recently updated row first, unknown dates last. */
function recencyDescent(left: PullRequestInboxRow, right: PullRequestInboxRow): number {
  const leftUpdated = sortableUpdatedAt(left);
  const rightUpdated = sortableUpdatedAt(right);
  const measured = Number(leftUpdated === null) - Number(rightUpdated === null);
  if (measured !== 0) return measured;
  if (leftUpdated === null || rightUpdated === null) return 0;
  return rightUpdated - leftUpdated;
}

/**
 * The default review queue: work that is green and approved, then green work still waiting on a
 * verdict, then everything else still open. Drafts stay in that third tier because their author
 * has not made them mergeable yet. Finished work follows open work when all states are visible.
 * A known conflict is never ready, whatever its checks, review or state say, so it stays at the
 * bottom. Within each tier, smaller measured diffs come first, then unknown sizes. Recency
 * breaks ties between equally sized diffs.
 */
export function rankPullRequestsByMergeReadiness(
  rows: ReadonlyArray<PullRequestInboxRow>,
): ReadonlyArray<PullRequestInboxRow> {
  return rows.toSorted((left, right) => {
    const byTier = mergeReadinessTier(left) - mergeReadinessTier(right);
    if (byTier !== 0) return byTier;
    const measured = Number(hasMeasuredSize(right)) - Number(hasMeasuredSize(left));
    const sized = left.additions + left.deletions - (right.additions + right.deletions);
    return measured || sized || right.updatedAt.localeCompare(left.updatedAt);
  });
}

function rankByTierThenRecency(
  rows: ReadonlyArray<PullRequestInboxRow>,
  tier: (row: PullRequestInboxRow) => number,
): ReadonlyArray<PullRequestInboxRow> {
  return rows.toSorted((left, right) => {
    const byTier = tier(left) - tier(right);
    if (byTier !== 0) return byTier;
    return recencyDescent(left, right);
  });
}

/** The author's queue: what is surely the author's to unblock sorts first. */
export function rankPullRequestsBlockedOnAuthor(
  rows: ReadonlyArray<PullRequestInboxRow>,
): ReadonlyArray<PullRequestInboxRow> {
  return rankByTierThenRecency(rows, (row) => {
    if (row.state !== "open") return 6;
    if (row.mergeability === "conflicting") return 0;
    if (row.reviewDecision === "changes-requested") return 1;
    if (row.checksState === "failing") return 2;
    if (row.isDraft) return 3;
    if (row.checksState === "passing" && row.reviewDecision === "approved") return 5;
    return 4;
  });
}

/** The reviewer's queue: open work first, newest first within it. */
export function rankPullRequestsBlockedOnReviewer(
  rows: ReadonlyArray<PullRequestInboxRow>,
): ReadonlyArray<PullRequestInboxRow> {
  return rankByTierThenRecency(rows, (row) => (row.state === "open" ? 0 : 1));
}

/** Newest or oldest by creation date, unknown dates last, recency breaking ties. */
function compareByAge(
  sort: "newest" | "oldest",
  left: PullRequestInboxRow,
  right: PullRequestInboxRow,
): number {
  const leftCreated = toSortableTimestamp(left.createdAt);
  const rightCreated = toSortableTimestamp(right.createdAt);
  const measured = Number(rightCreated !== null) - Number(leftCreated !== null);
  const dated = (leftCreated ?? 0) - (rightCreated ?? 0);
  return measured || (sort === "newest" ? -dated : dated) || recencyDescent(left, right);
}

/** Largest or smallest by measured diff size, unknown sizes last, recency breaking ties. */
function compareBySize(
  sort: "largest" | "smallest",
  left: PullRequestInboxRow,
  right: PullRequestInboxRow,
): number {
  const measured = Number(hasMeasuredSize(right)) - Number(hasMeasuredSize(left));
  const sized = left.additions + left.deletions - (right.additions + right.deletions);
  return measured || (sort === "largest" ? -sized : sized) || recencyDescent(left, right);
}

/** The date and diff-size orderings, which are comparator work rather than a tier rank. */
function compareByDimension(
  sort: "newest" | "oldest" | "largest" | "smallest",
  left: PullRequestInboxRow,
  right: PullRequestInboxRow,
): number {
  if (sort === "newest" || sort === "oldest") return compareByAge(sort, left, right);
  return compareBySize(sort, left, right);
}

/**
 * One section's rows under the selected ordering. A section's rows all carry its group, so the
 * blocked sort reads the role off the first row: the reviewer's queue for Reviewing, the
 * author's for Authored. Others keeps its recency order there, which is what upstream's
 * involvement fall-back resolves to for a viewer with no role in the change request.
 *
 * Ready and blocked stand down while a search is on: the host's relevance order answers "which
 * of these matches", and a readiness tier would answer over it.
 */
export function sortPullRequestInboxRows(
  rows: ReadonlyArray<PullRequestInboxRow>,
  sort: PullRequestListSort | undefined,
  searchText: string,
): ReadonlyArray<PullRequestInboxRow> {
  if (sort === undefined || sort === "updated") return rows;
  if ((sort === "ready" || sort === "blocked") && searchText.trim().length > 0) return rows;
  if (sort === "ready") return rankPullRequestsByMergeReadiness(rows);
  if (sort === "blocked") {
    const group: PullRequestInboxGroup | undefined = rows[0]?.group;
    if (group === "authored") return rankPullRequestsBlockedOnAuthor(rows);
    if (group === "reviewing") return rankPullRequestsBlockedOnReviewer(rows);
    return rows;
  }
  return rows.toSorted((left, right) => compareByDimension(sort, left, right));
}
